<template>
    <div class="shipment_detail">
        <!-- 头部 -->
        <div class="detail_header">
            <div class="back_btn" @click="$router.back()">
                <van-icon name="arrow-left" size="24" />
            </div>
            <span class="header_title">发货详情</span>
            <div class="placeholder"></div>
        </div>

        <!-- 详情内容 -->
        <div class="detail_content" v-if="detail.id">
            <!-- 基本信息 -->
            <div class="detail_section">
                <div class="section_title">基本信息</div>
                <div class="detail_row">
                    <span class="label">发货单号</span>
                    <span class="value">#{{detail.id}}</span>
                </div>
                <div class="detail_row">
                    <span class="label">类型</span>
                    <van-tag :type="detail.shipment_type === 'product' ? 'primary' : 'success'">
                        {{detail.shipment_type === 'product' ? '产品' : '物料'}}
                    </van-tag>
                </div>
                <div class="detail_row">
                    <span class="label">名称</span>
                    <span class="value">{{detail.record_name || '-'}}</span>
                </div>
                <div class="detail_row">
                    <span class="label">发货数量</span>
                    <span class="value highlight">{{detail.shipment_num || 0}} 件</span>
                </div>
                <div class="detail_row">
                    <span class="label">销售人员</span>
                    <span class="value">{{detail.sale_user_name || '-'}}</span>
                </div>              
                <div class="detail_row">
                    <span class="label">发货时间</span>
                    <span class="value">{{detail.create_time | formatTime}}</span>
                </div>
            </div>

            <!-- 确认流程 -->
            <div class="detail_section">
                <div class="section_title">
                    确认流程
                    <span class="step_badge" :class="'step' + getCurrentStep(detail)">
                        {{getStepText(detail)}}
                    </span>
                </div>
                <div class="flow_list">
                    <div class="flow_item" :class="{active: getCurrentStep(detail) === 1, completed: detail.debug_sure_user_id}">
                        <div class="flow_icon">
                            <van-icon :name="detail.debug_sure_user_id ? 'success' : '1'" />
                        </div>
                        <div class="flow_info">
                            <div class="flow_name">调试确认</div>
                            <div class="flow_user" v-if="detail.debug_sure_user_name">{{detail.debug_sure_user_name}}</div>
                            <div class="flow_pending" v-else>待确认</div>
                        </div>
                        <van-button
                            v-if="!detail.debug_sure_user_id && getCurrentStep(detail) === 1 && canConfirm('debug')"
                            size="small"
                            type="primary"
                            @click="confirmStep('debug')"
                        >
                            确认
                        </van-button>
                    </div>
                    <div class="flow_item" :class="{active: getCurrentStep(detail) === 2, completed: detail.need_sure_user_id}">
                        <div class="flow_icon">
                            <van-icon :name="detail.need_sure_user_id ? 'success' : '2'" />
                        </div>
                        <div class="flow_info">
                            <div class="flow_name">需求确认</div>
                            <div class="flow_user" v-if="detail.need_sure_user_name">{{detail.need_sure_user_name}}</div>
                            <div class="flow_pending" v-else>待确认</div>
                        </div>
                        <van-button
                            v-if="!detail.need_sure_user_id && getCurrentStep(detail) === 2 && canConfirm('need')"
                            size="small"
                            type="primary"
                            @click="confirmStep('need')"
                        >
                            确认
                        </van-button>
                    </div>
                    <div class="flow_item" :class="{active: getCurrentStep(detail) === 3, completed: detail.quality_sure_user_id}">
                        <div class="flow_icon">
                            <van-icon :name="detail.quality_sure_user_id ? 'success' : '3'" />
                        </div>
                        <div class="flow_info">
                            <div class="flow_name">质量确认</div>
                            <div class="flow_user" v-if="detail.quality_sure_user_name">{{detail.quality_sure_user_name}}</div>
                            <div class="flow_pending" v-else>待确认</div>
                        </div>
                        <van-button
                            v-if="!detail.quality_sure_user_id && getCurrentStep(detail) === 3 && canConfirm('quality')"
                            size="small"
                            type="primary"
                            @click="confirmStep('quality')"
                        >
                            确认
                        </van-button>
                    </div>
                    <div class="flow_item" :class="{active: getCurrentStep(detail) === 4, completed: detail.shipment_sure_user_id}">
                        <div class="flow_icon">
                            <van-icon :name="detail.shipment_sure_user_id ? 'success' : '4'" />
                        </div>
                        <div class="flow_info">
                            <div class="flow_name">发货确认</div>
                            <div class="flow_user" v-if="detail.shipment_sure_user_name">{{detail.shipment_sure_user_name}}</div>
                            <div class="flow_pending" v-else>待确认</div>
                        </div>
                        <van-button
                            v-if="!detail.shipment_sure_user_id && getCurrentStep(detail) === 4 && canConfirm('shipment')"
                            size="small"
                            type="primary"
                            @click="confirmStep('shipment')"
                        >
                            确认
                        </van-button>
                    </div>
                </div>
            </div>

            <!-- 收货信息 -->
            <div class="detail_section" v-if="detail.shipment_address_info">
                <div class="section_title">收货信息</div>
                <div class="detail_row">
                    <span class="value">{{detail.shipment_address_info}}</span>
                </div>
            </div>

            <!-- 备注 -->
            <div class="detail_section" v-if="detail.remark">
                <div class="section_title">备注</div>
                <div class="remark_text">{{detail.remark}}</div>
            </div>

            <!-- 发货照片 -->
            <div class="detail_section">
                <div class="section_title">
                    <span>发货照片</span>
                    <van-button size="mini" type="primary" icon="plus" :loading="uploading"
                        loading-text="上传中..." @click="triggerPhotoUpload">
                        补充照片
                    </van-button>
                </div>
                <div class="photo_requirement">！发货单拍照放第一</div>
                <div class="photo_grid" v-if="photoList.length > 0">
                    <div class="photo_item" v-for="(photo, idx) in photoList" :key="idx" @click="previewPhotos(idx)">
                        <img :src="photo" />
                        <div class="photo_del" @click.stop="confirmRemovePhoto(idx)">
                            <van-icon name="cross" size="14" color="#fff" />
                        </div>
                    </div>
                </div>
                <div class="photo_empty" v-else>暂无发货照片，点击上方按钮补充</div>
                <input ref="photoInput" type="file" accept="image/*" multiple style="display:none" @change="handlePhotoSelect" />
            </div>
        </div>

        <!-- 加载中 -->
        <div class="loading_state" v-else>
            <van-loading type="spinner" size="40px" />
            <span>加载中...</span>
        </div>
    </div>
</template>

<script>
import studio_api from '@/api/teaching_studio_teaching_studio_api.js'
import studio_tool_api from '@/api/teaching_studio_tool.js'
import until from '@/api/until.js'
import { ImagePreview } from 'vant'
import {
    formatIotmaxTimestamp,
    getIotmaxFormAuth,
    getIotmaxShipmentStep,
    getIotmaxShipmentStepText,
    hasIotmaxStockRole,
    parseIotmaxAddressInfo,
    parseIotmaxImageList
} from '@/utils/iotmaxStock.js'

export default {
    name: 'ShipmentRecordDetail',
    data() {
        return {
            detail: {},
            photoList: [],
            uploading: false,
            uploadConfig: { host: '', policy: '', accessid: '', signature: '', dir: '' }
        }
    },
    filters: {
        formatTime(timestamp) {
            return formatIotmaxTimestamp(timestamp, { dateOnly: true })
        }
    },
    mounted() {
        document.title = "发货详情"
        this.getDetail()
    },
    methods: {
        // 获取详情
        async getDetail() {
            const id = this.$route.query.id
            if (!id) {
                this.$toast('缺少ID参数')
                return
            }

            this.$toast.loading({ message: '加载中...' })
            
            try {
                let res = await studio_api.Teaching_studio_apiIotmaxShipmentDetailGet({
                    app_id: this.$root.app_id,
                    id: id
                })
                if (res.errcode == 0 && res.other) {
                    this.detail = res.other
                    this.parsePhotos()
                } else {
                    this.$toast('获取详情失败')
                }
            } catch (e) {
                console.error('获取详情失败', e)
                this.$toast('获取详情失败')
            } finally {
                this.$toast.clear()
            }
        },

        // 解析地址信息
        parseAddressInfo(addressInfo) {
            return parseIotmaxAddressInfo(addressInfo)
        },

        // 获取当前步骤
        getCurrentStep(item) {
            return getIotmaxShipmentStep(item)
        },

        // 获取步骤文本
        getStepText(item) {
            return getIotmaxShipmentStepText(item)
        },

        // 判断是否有确认权限
        canConfirm(stepType) {
            return hasIotmaxStockRole(this.$root.userInfo, stepType, this.$root.isSuperRoot)
        },

        // 解析照片列表
        parsePhotos() {
            this.photoList = parseIotmaxImageList(this.detail.shipment_photos)
        },

        // 触发文件选择
        triggerPhotoUpload() {
            this.$refs.photoInput.click()
        },

        // 文件选中后上传
        async handlePhotoSelect(e) {
            const files = Array.from(e.target.files)
            if (!files.length) return
            e.target.value = ''

            this.uploading = true
            this.$toast.loading({ message: '上传中...', duration: 0 })

            try {
                // 先获取阿里云上传凭证
                await this.getAliyunToken()

                // 逐张上传
                for (const file of files) {
                    await this.uploadOnePhoto(file)
                }

                // 保存到服务器
                await this.savePhotos()
                this.$toast.clear()
                this.$toast.success('上传成功')
            } catch (err) {
                this.$toast.clear()
                console.error('上传失败', err)
                this.$toast('上传失败，请重试')
            } finally {
                this.uploading = false
            }
        },

        // 获取阿里云上传Token
        async getAliyunToken() {
            const res = await studio_api.Teaching_studio_apiMediaAliyunFileUpGet({
                app_id: this.$root.app_id
            })
            if (res.errcode === 0 && res.other) {
                const cfg = JSON.parse(res.other)
                this.uploadConfig = {
                    host: cfg.host,
                    policy: cfg.policy,
                    accessid: cfg.accessid,
                    signature: cfg.signature,
                    dir: cfg.dir
                }
            } else {
                throw new Error('获取上传凭证失败')
            }
        },

        // 上传单张照片到OSS
        uploadOnePhoto(file) {
            return new Promise((resolve, reject) => {
                if (file.size > 5 * 1024 * 1024) {
                    this.$toast(`${file.name} 超过5MB，已跳过`)
                    resolve()
                    return
                }

                const suffix = file.name.substring(file.name.lastIndexOf('.'))
                const rand = Math.random().toString(36).slice(2, 10)
                const key = `${this.uploadConfig.dir}/${Date.now()}_${rand}${suffix}`

                const formData = new FormData()
                formData.append('key', key)
                formData.append('policy', this.uploadConfig.policy)
                formData.append('OSSAccessKeyId', this.uploadConfig.accessid)
                formData.append('signature', this.uploadConfig.signature)
                formData.append('success_action_status', '200')
                formData.append('file', file)

                fetch(this.uploadConfig.host + '/', { method: 'POST', body: formData })
                    .then(res => {
                        if (res.ok) {
                            this.photoList.push(this.uploadConfig.host + '/' + key)
                            resolve()
                        } else {
                            reject(new Error('OSS上传失败: ' + res.status))
                        }
                    })
                    .catch(reject)
            })
        },

        // 保存照片列表到服务器
        async savePhotos() {
            const { computer_code, salt } = await getIotmaxFormAuth(studio_tool_api, until)

            const res = await studio_api.Teaching_studio_apiIotmaxShipmentUpdatePut({
                app_id: this.$root.app_id,
                computer_code,
                salt,
                id: this.detail.id,
                shipment_type: this.detail.shipment_type,
                record_id: this.detail.record_id || 0,
                shipment_num: this.detail.shipment_num,
                studio_id: this.detail.studio_id || 0,
                sale_user_id: this.detail.sale_user_id,
                shipment_address_info: this.detail.shipment_address_info || '',
                remark: this.detail.remark || '',
                shipment_photos: this.photoList.join(',')
            })

            if (res.errcode !== 0) {
                throw new Error(res.errmsg || '保存失败')
            }

            // 更新本地detail
            this.detail.shipment_photos = this.photoList.join(',')
        },

        // 预览照片
        previewPhotos(startIndex) {
            ImagePreview({
                images: this.photoList,
                startPosition: startIndex,
                closeable: true
            })
        },

        // 确认删除照片
        confirmRemovePhoto(idx) {
            this.$dialog.confirm({
                title: '删除照片',
                message: '确定删除这张照片吗？'
            }).then(async () => {
                this.photoList.splice(idx, 1)
                this.$toast.loading({ message: '保存中...', duration: 0 })
                try {
                    await this.savePhotos()
                    this.$toast.clear()
                    this.$toast.success('删除成功')
                } catch (e) {
                    this.$toast.clear()
                    this.$toast('删除失败')
                    // 恢复
                    this.parsePhotos()
                }
            }).catch(() => {})
        },

        // 确认步骤
        async confirmStep(stepType) {
            const stepNames = {
                debug: '调试确认',
                need: '需求确认',
                quality: '质量确认',
                shipment: '发货确认'
            }

            try {
                await this.$dialog.confirm({
                    title: '确认操作',
                    message: `确定要${stepNames[stepType]}吗？`
                })
            } catch (e) {
                return
            }

            this.$toast.loading({ message: '确认中...', duration: 0 })

            try {
                const { computer_code, salt } = await getIotmaxFormAuth(studio_tool_api, until)

                // 调用对应的确认接口
                let res
                if (stepType === 'debug') {
                    res = await studio_api.Teaching_studio_apiIotmaxShipmentDebug_surePut({
                        app_id: this.$root.app_id,
                        computer_code,
                        salt,
                        id: this.detail.id
                    })
                } else if (stepType === 'need') {
                    res = await studio_api.Teaching_studio_apiIotmaxShipmentNeed_surePut({
                        app_id: this.$root.app_id,
                        computer_code,
                        salt,
                        id: this.detail.id
                    })
                } else if (stepType === 'quality') {
                    res = await studio_api.Teaching_studio_apiIotmaxShipmentQuality_surePut({
                        app_id: this.$root.app_id,
                        computer_code,
                        salt,
                        id: this.detail.id
                    })
                } else if (stepType === 'shipment') {
                    res = await studio_api.Teaching_studio_apiIotmaxShipmentShipment_surePut({
                        app_id: this.$root.app_id,
                        computer_code,
                        salt,
                        id: this.detail.id
                    })
                }

                this.$toast.clear()

                if (res.errcode === 0) {
                    this.$toast.success('确认成功')
                    // 重新获取详情
                    this.getDetail()
                } else {
                    this.$toast(res.errmsg || '确认失败')
                }
            } catch (e) {
                this.$toast.clear()
                console.error('确认失败', e)
                this.$toast('确认失败')
            }
        }
    }
}
</script>

<style scoped>
.shipment_detail {
    width: 100%;
    height: 100vh;
    background: #F6F6F6;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.detail_header {
    width: 100%;
    height: 76px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    box-sizing: border-box;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    position: sticky;
    top: 0;
    z-index: 100;
}

.back_btn i {
    font-size: 36px;
    color: #333;
}

.header_title {
    font-size: 30px;
    font-weight: bold;
    color: #333;
}

.placeholder {
    width: 36px;
}

.detail_content {
    padding: 14px 16px;
}

.detail_section {
    background: #fff;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 10px;
}

.section_title {
    font-size: 28px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 2px solid #4E89FF;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.detail_row {
    display: flex;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f5f5f5;
}

.detail_row:last-child {
    border-bottom: none;
}

.detail_row .label {
    width: 130px;
    font-size: 26px;
    color: #999;
    flex-shrink: 0;
}

.detail_row .value {
    flex: 1;
    font-size: 26px;
    color: #333;
    word-break: break-all;
}

.detail_row .value.highlight {
    color: #F44336;
    font-weight: bold;
}

.step_badge {
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 22px;
}

/* 步骤颜色统一规范 */
.step_badge.step1 { background: #F5F5F5; color: #999; }
.step_badge.step2 { background: #FFF3E0; color: #FF9800; }
.step_badge.step3 { background: #E3F2FD; color: #2196F3; }
.step_badge.step4 { background: #E0F7FA; color: #00BCD4; }
.step_badge.step5 { background: #4CAF50; color: #fff; }

.flow_list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.flow_item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    background: #f9f9f9;
    border-radius: 10px;
    border-left: 3px solid #ddd;
}

.flow_item.active {
    background: #FFF8E1;
    border-left-color: #FF9800;
}

.flow_item.completed {
    background: #E8F5E9;
    border-left-color: #4CAF50;
}

.flow_item.completed .flow_name {
    color: #2E7D32;
}

.flow_icon {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #999;
    flex-shrink: 0;
}

.flow_item.active .flow_icon {
    background: #FF9800;
    color: #fff;
}

.flow_item.completed .flow_icon {
    background: #4CAF50;
    color: #fff;
}

.flow_info {
    flex: 1;
    min-width: 0;
}

.flow_name {
    font-size: 26px;
    color: #333;
    font-weight: 600;
}

.flow_user {
    font-size: 22px;
    color: #4CAF50;
    margin-top: 2px;
}

.flow_pending {
    font-size: 22px;
    color: #999;
    margin-top: 2px;
}

.remark_text {
    font-size: 26px;
    color: #666;
    line-height: 1.5;
    padding: 10px 12px;
    background: #f9f9f9;
    border-radius: 8px;
}

.loading_state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-top: 160px;
}

.loading_state span {
    margin-top: 16px;
    font-size: 26px;
    color: #999;
}

/* 发货照片 */
.photo_grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.photo_requirement {
    margin: -2px 0 12px;
    color: #ee0a24;
    font-size: 24px;
    line-height: 1.4;
    font-weight: 500;
}

.photo_item {
    position: relative;
    width: 108px;
    height: 108px;
    border-radius: 10px;
    overflow: hidden;
    background: #f5f5f5;
    border: 1px solid #eaeaea;
}

.photo_item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.photo_item:active {
    opacity: 0.8;
    transform: scale(0.97);
}

.photo_del {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
}

.photo_del:active {
    background: rgba(244, 67, 54, 0.9);
}

.photo_empty {
    font-size: 24px;
    color: #999;
    padding: 12px 0;
}
</style>
